import { OnDestroy, QueryList, ElementRef, Directive, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { AuthentificationService } from '../../providers/Global/authentification.service';
import { EditService } from '../../providers/edit/edit.service';

import { MatTableDataSource } from '@angular/material/table';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Globals } from '../../ressources/variables/globals';
import { FinalizeDialogComponent } from './finalizeDialog.component';
import { UnlockConfirmDialogComponent } from './unlockConfirmDialog.component';
import { SendFinalizedPlaylistConfirmDialogComponent } from './sendFinalizedPlaylistConfirmDialog.component';

export interface PlaylistVote {
  imedia: number;
  id_user: number;
  score_vote: number;
  user_name: string;
}

/**
 * Classe de base abstraite partagée entre EditPreselectMonthlyPlaylistComponent
 * et EditPreselectCategoryComponent.
 *
 * Contient toute la logique commune :
 *  - Système de votes (scores bayésiens, map de votes)
 *  - Lecteurs audio (extrait MP3 + HLS)
 *  - Mode édition (finalisation, verrouillage, drag & drop, limite de sélection)
 *
 * Chaque sous-classe est responsable de :
 *  - Son propre ngOnInit() et chargement initial
 *  - Fournir les références de vue (paginator, sort, audioPlayers, audioExtraits)
 *  - Implémenter getPlaylistId() et getStationId()
 */
@Directive()
export abstract class PlaylistVoteBaseComponent implements OnDestroy {

  // ============================================================================
  // Références de vue abstraites (fournies par les sous-classes via @ViewChild)
  // ============================================================================
  protected abstract get paginator(): MatPaginator | undefined;
  protected abstract get sort(): MatSort;
  protected abstract get audioPlayers(): QueryList<ElementRef<HTMLAudioElement>>;
  protected abstract get audioExtraits(): QueryList<ElementRef<HTMLAudioElement>>;

  // ============================================================================
  // Colonnes du tableau (spécifiques à chaque page)
  // ============================================================================
  abstract displayedColumns: string[];
  abstract displayedColumnsEditMode: string[];

  // ============================================================================
  // Permission requise (spécifique à chaque page)
  // ============================================================================
  protected abstract get requiredPermission(): string;

  // ============================================================================
  // Méthodes abstraites — implémentées par chaque sous-classe
  // ============================================================================

  /** Génère l'identifiant unique de la playlist (ex : "01_2026" ou "category_pop"). */
  abstract getPlaylistId(): string;

  /**
   * Retourne l'ID de la station à utiliser dans les appels API.
   * - EditPreselectMonthlyPlaylist : retourne this.stationSelected.id
   * - EditPreselectCategory : retourne 0 (pas de filtrage par station)
   */
  abstract getStationId(): number;

  // ============================================================================
  // Propriétés communes
  // ============================================================================
  permissions: Array<string> = [];

  medias: any[] = [];
  mediasMatTable: MatTableDataSource<any>;

  positionsCache: Map<number, number> = new Map();

  extraitLocation = Globals.websiteRacine + '/storage/winmedia/extraits/';
  hlsLocation = Globals.aodRacine + '/SD/';

  loading = false;

  userId: number;
  userName: string;

  playlistVotes: PlaylistVote[] = [];
  votesByMedia: Map<number, PlaylistVote[]> = new Map();

  globalAverageScore: number = 0;
  confidenceWeight: number = 10;

  isEditMode = false;
  selectionLimit: number | null = null;
  finalizedPlaylistId: number | null = null;
  isLocked = false;

  isSentWinmedia = false;
  isSentLaravel = false;
  sentAt: string | null = null;

  isPlaying: { [key: string]: boolean } = {};
  currentTime: { [key: string]: number } = {};
  duration: { [key: string]: number } = {};

  protected selectionLimitSubject = new Subject<number | null>();
  protected destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    protected authentificationService: AuthentificationService,
    protected editService: EditService,
    protected dialog: MatDialog,
    protected snackBar: MatSnackBar,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    this.permissions = this.authentificationService.permissionsSource.getValue();
    iconRegistry.addSvgIcon('play', sanitizer.bypassSecurityTrustResourceUrl('assets/icon/play.svg'));
    iconRegistry.addSvgIcon('stop', sanitizer.bypassSecurityTrustResourceUrl('assets/icon/stop.svg'));
    iconRegistry.addSvgIcon('thumbs-up', sanitizer.bypassSecurityTrustResourceUrl('assets/icon/thumbs-up.svg'));
    iconRegistry.addSvgIcon('thumbs-down', sanitizer.bypassSecurityTrustResourceUrl('assets/icon/thumbs-down.svg'));
    iconRegistry.addSvgIcon('drag-handle', sanitizer.bypassSecurityTrustResourceUrl('assets/icon/drag-handle.svg'));

    this.selectionLimitSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.saveSelectionLimit(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================================================
  // Verrouillage automatique (surcharger dans la sous-classe si nécessaire)
  // ============================================================================

  /**
   * Vérifie si la playlist doit être automatiquement verrouillée.
   * Par défaut : false.
   * EditPreselectMonthlyPlaylistComponent surcharge cette méthode avec la logique de date.
   */
  isAutoLocked(): boolean {
    return false;
  }

  get isEffectivelyLocked(): boolean {
    return this.isLocked || this.isAutoLocked();
  }

  // ============================================================================
  // Chargement commun des données (template method)
  // ============================================================================

  /**
   * Méthode partagée pour charger les médias, les votes et la playlist finalisée.
   * La fonction mediasLoader est fournie par la sous-classe et charge les médias
   * selon son propre critère (mois ou catégorie).
   */
  protected loadDataForSelection(mediasLoader: () => Promise<any>): void {
    if (!this.permissions.includes(this.requiredPermission)) {
      this.router.navigate(['']).then(() => {});
      return;
    }

    this.loading = true;
    this.mediasMatTable = new MatTableDataSource<any>([]);
    this.finalizedPlaylistId = null;
    this.selectionLimit = null;
    this.isLocked = false;
    this.isEditMode = false;
    this.isSentWinmedia = false;
    this.isSentLaravel = false;
    this.sentAt = null;

    const user = this.authentificationService.userSource.getValue();
    this.userId = user.uid;
    this.userName = user.name;

    const idPlaylist = this.getPlaylistId();
    const stationId = this.getStationId();

    Promise.all([
      mediasLoader(),
      this.editService.getPlaylistVotes(idPlaylist),
      this.editService.getFinalizedPlaylist(idPlaylist, stationId)
    ])
      .then(([mediasRes, votesRes, finalizedRes]) => {
        this.medias = mediasRes.recordset;
        this.playlistVotes = votesRes;
        this.buildVotesMap();
        this.calculatePositions();

        if (finalizedRes.playlist) {
          this.finalizedPlaylistId = finalizedRes.playlist.id;
          this.isLocked = finalizedRes.playlist.is_locked || false;
          this.selectionLimit = finalizedRes.playlist.selection_limit;
          this.isSentWinmedia = finalizedRes.playlist.is_sent_winmedia || false;
          this.isSentLaravel = finalizedRes.playlist.is_sent_laravel || false;
          this.sentAt = finalizedRes.playlist.sent_at || null;

          const positionsArray = finalizedRes.playlist.positions
            ? finalizedRes.playlist.positions.split(',').map((id: string) => parseInt(id, 10))
            : [];

          const positionsMap = new Map<number, number>();
          positionsArray.forEach((imedia: number, index: number) => {
            positionsMap.set(imedia, index + 1);
          });

          this.medias = this.medias.map((media: any) => ({
            ...media,
            position: positionsMap.get(media.imedia) || null
          }));

          this.medias.sort((a: any, b: any) => (a.position || 999999) - (b.position || 999999));
        }

        this.setupMatTable();
        this.loading = false;
      })
      .catch(() => this.loading = false);
  }

  protected setupMatTable(): void {
    this.mediasMatTable = new MatTableDataSource<any>(this.medias);
    this.mediasMatTable.paginator = this.paginator;

    if (this.paginator) {
      this.paginator._intl.itemsPerPageLabel = 'Eléments affichés par page';
      this.paginator._intl.nextPageLabel = '';
      this.paginator._intl.previousPageLabel = '';
      this.paginator._intl.firstPageLabel = '';
      this.paginator._intl.lastPageLabel = '';
    }

    this.mediasMatTable.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'modify': return new Date(item.modify);
        case 'score': return this.getBayesianSortValue(item.imedia);
        case 'average': return this.getAverageSortValue(item.imedia);
        case 'release': return item.release_date;
        case 'performer_title': return item.performer + item.title;
        case 'vote': return this.getUserScore(item.imedia);
        case 'votes_detail': return this.getVoteCount(item.imedia);
        case 'position': return item.position !== undefined && item.position !== null
          ? item.position
          : this.getPositionSortValue(item.imedia);
        default: return item[property];
      }
    };

    this.mediasMatTable.sort = this.sort;

    const sortHeaderDefaults = ['imedia', 'release', 'modify', 'score', 'average'];
    sortHeaderDefaults.forEach(col => {
      const header = this.sort?.sortables?.get(col);
      if (header) { header.start = 'desc'; }
    });
  }

  // ============================================================================
  // Votes et scores bayésiens
  // ============================================================================

  buildVotesMap(): void {
    this.votesByMedia.clear();
    for (const vote of this.playlistVotes) {
      if (!this.votesByMedia.has(vote.imedia)) {
        this.votesByMedia.set(vote.imedia, []);
      }
      this.votesByMedia.get(vote.imedia)!.push(vote);
    }
    this.calculateGlobalAverage();
  }

  calculateGlobalAverage(): void {
    if (this.playlistVotes.length === 0) {
      this.globalAverageScore = 0;
      return;
    }
    const sum = this.playlistVotes.reduce((acc, v) => acc + v.score_vote, 0);
    this.globalAverageScore = sum / this.playlistVotes.length;
  }

  calculatePositions(): void {
    this.positionsCache.clear();
    const mediasWithScores = this.medias.map(media => ({
      imedia: media.imedia,
      sortValue: this.getBayesianSortValue(media.imedia),
      voteCount: this.getVoteCount(media.imedia)
    }));
    mediasWithScores.sort((a, b) => b.sortValue - a.sortValue);

    let position = 1;
    for (const media of mediasWithScores) {
      if (media.voteCount > 0) {
        this.positionsCache.set(media.imedia, position);
        position++;
      } else {
        this.positionsCache.set(media.imedia, 0);
      }
    }
  }

  getPosition(imedia: number): number {
    return this.positionsCache.get(imedia) || 0;
  }

  getPositionSortValue(imedia: number): number {
    const position = this.getPosition(imedia);
    return position === 0 ? 999999 : position;
  }

  getUserScore(imedia: number): number {
    const votes = this.votesByMedia.get(imedia);
    if (!votes) { return 0; }
    const userVote = votes.find(v => v.id_user === this.userId);
    return userVote ? userVote.score_vote : 0;
  }

  getAverageScore(imedia: number): number {
    const votes = this.votesByMedia.get(imedia);
    if (!votes || votes.length === 0) { return 0; }
    const sum = votes.reduce((acc, v) => acc + v.score_vote, 0);
    return sum / votes.length;
  }

  getBayesianScore(imedia: number): number {
    const votes = this.votesByMedia.get(imedia);
    if (!votes || votes.length === 0) { return 0; }
    const v = votes.length;
    const s = votes.reduce((acc, vote) => acc + vote.score_vote, 0) / v;
    const m = this.globalAverageScore;
    const C = this.confidenceWeight;
    return (v * s + m * C) / (v + C);
  }

  getBayesianSortValue(imedia: number): number {
    const votes = this.votesByMedia.get(imedia);
    if (!votes || votes.length === 0) { return 0; }
    return this.getBayesianScore(imedia) * 1000 + votes.length;
  }

  getAverageSortValue(imedia: number): number {
    const votes = this.votesByMedia.get(imedia);
    if (!votes || votes.length === 0) { return 0; }
    return this.getAverageScore(imedia) * 1000 + votes.length;
  }

  getVoteCount(imedia: number): number {
    const votes = this.votesByMedia.get(imedia);
    return votes ? votes.length : 0;
  }

  getMediaVotes(imedia: number): PlaylistVote[] {
    return this.votesByMedia.get(imedia) || [];
  }

  hasUserVoted(imedia: number): boolean {
    return this.getUserScore(imedia) > 0;
  }

  // ============================================================================
  // Export CSV
  // ============================================================================

  exportToCsv(): void {
    const separator = ';';
    const escapeCsv = (value: any): string => {
      const str = value === null || value === undefined ? '' : String(value);
      if (/[";\n]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const headers = [
      'Position', 'Score bayésien', 'Nb votes', 'Score moyen',
      'Artiste', 'Titre', 'Version', 'Genre', 'Mood3', 'Voix',
      'Propriétés', 'Date sortie', 'Import', 'Propriétaire',
      'Votes détail', 'IMedia'
    ];

    const sortedMedias = [...this.medias].sort(
      (a, b) => this.getBayesianSortValue(b.imedia) - this.getBayesianSortValue(a.imedia)
    );

    const rows = sortedMedias.map(media => {
      const position = media.position !== undefined && media.position !== null
        ? media.position
        : this.getPosition(media.imedia);
      const votesDetail = this.getMediaVotes(media.imedia)
        .map(v => `${v.user_name}: ${v.score_vote}`)
        .join(', ');

      return [
        position > 0 ? position : '',
        this.getBayesianScore(media.imedia).toFixed(3),
        this.getVoteCount(media.imedia),
        this.getAverageScore(media.imedia).toFixed(2),
        media.performer,
        media.title,
        media.version,
        media.genre,
        media.mood3,
        media.voix,
        media.proprietes,
        media.release_date,
        media.modify,
        media.proprietaire,
        votesDetail,
        media.imedia
      ].map(escapeCsv).join(separator);
    });

    const bom = String.fromCharCode(0xFEFF);
    const csvContent = bom + [headers.join(separator), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `playlist_${this.getPlaylistId()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  setScore(element: any, score: number): void {
    if (this.isEffectivelyLocked) {
      const message = this.isLocked
        ? 'Les votes sont verrouillés pour cette playlist'
        : 'Les votes sont automatiquement verrouillés (délai dépassé)';
      this.snackBar.open(message, '', { duration: 3000 });
      return;
    }

    const idPlaylist = this.getPlaylistId();
    const currentScore = this.getUserScore(element.imedia);

    if (currentScore === score) {
      this.editService.deletePlaylistVote(idPlaylist, element.imedia)
        .then(() => { this.removeVoteFromMap(element.imedia, this.userId); })
        .catch(err => { console.error('Erreur lors de la suppression du vote:', err); });
    } else {
      this.editService.upsertPlaylistVote(idPlaylist, element.imedia, score)
        .then(() => { this.updateVoteInMap(element.imedia, this.userId, this.userName, score); })
        .catch(err => { console.error('Erreur lors de l\'enregistrement du vote:', err); });
    }
  }

  updateVoteInMap(imedia: number, userId: number, userName: string, score: number): void {
    let votes = this.votesByMedia.get(imedia);
    if (!votes) {
      votes = [];
      this.votesByMedia.set(imedia, votes);
    }

    const existingVoteIndex = votes.findIndex(v => v.id_user === userId);
    if (existingVoteIndex >= 0) {
      const globalIndex = this.playlistVotes.findIndex(v => v.imedia === imedia && v.id_user === userId);
      if (globalIndex >= 0) { this.playlistVotes[globalIndex].score_vote = score; }
      votes[existingVoteIndex].score_vote = score;
    } else {
      const newVote: PlaylistVote = { imedia, id_user: userId, score_vote: score, user_name: userName };
      votes.push(newVote);
      this.playlistVotes.push(newVote);
    }

    this.calculateGlobalAverage();
    this.calculatePositions();
  }

  removeVoteFromMap(imedia: number, userId: number): void {
    const votes = this.votesByMedia.get(imedia);
    if (votes) {
      const index = votes.findIndex(v => v.id_user === userId);
      if (index >= 0) { votes.splice(index, 1); }
    }

    const globalIndex = this.playlistVotes.findIndex(v => v.imedia === imedia && v.id_user === userId);
    if (globalIndex >= 0) { this.playlistVotes.splice(globalIndex, 1); }

    this.calculateGlobalAverage();
    this.calculatePositions();
  }

  // ============================================================================
  // Affichage des étoiles
  // ============================================================================

  getStarColor(starIndex: number, score: number, muted: boolean = false): string {
    const starThreshold = (starIndex - 1) * 2;
    if (score > starThreshold) {
      if (muted) {
        if (score <= 3) { return '#E57373'; }
        if (score <= 6) { return '#FFB74D'; }
        return '#66BB6A';
      } else {
        if (score <= 3) { return '#C62828'; }
        if (score <= 6) { return '#FF8F00'; }
        return '#2E7D32';
      }
    }
    return '#3d465a';
  }

  getStarDisplay(starIndex: number, score: number): string {
    if (score >= starIndex * 2) { return '★'; }
    if (score >= starIndex * 2 - 1) { return '⯨'; }
    return '☆';
  }

  extractValues(valueString: string): string[] {
    if (!valueString || valueString.trim() === '') { return []; }
    return valueString.split('/').filter(val => val.trim() !== '');
  }

  // ============================================================================
  // Lecteurs audio
  // ============================================================================

  stopAllPlayersExcept(currentPlayerId: string): void {
    Object.keys(this.isPlaying).forEach(playerId => {
      if (playerId !== currentPlayerId) {
        this.isPlaying[playerId] = false;
        this.currentTime[playerId] = 0;
      }
    });

    this.audioExtraits?.forEach((audioRef) => {
      const audio = audioRef.nativeElement;
      const playerId = audio.dataset['playerId'];
      if (playerId !== currentPlayerId) {
        if (!audio.paused) { audio.pause(); }
        audio.currentTime = 0;
      }
    });

    this.audioPlayers?.forEach((audioRef) => {
      const audio = audioRef.nativeElement;
      const playerId = audio.dataset['playerId'];
      if (playerId !== currentPlayerId) {
        if (!audio.paused) { audio.pause(); }
        audio.currentTime = 0;
      }
    });
  }

  togglePlay(audioElement: HTMLAudioElement, playerId: string): void {
    if (this.isPlaying[playerId]) {
      audioElement.pause();
      this.isPlaying[playerId] = false;
    } else {
      this.stopAllPlayersExcept(playerId);
      audioElement.play();
      this.isPlaying[playerId] = true;
    }
  }

  togglePlayHLS(audioElement: HTMLAudioElement, element: any, playerId: string): void {
    if (this.isPlaying[playerId]) {
      audioElement.pause();
      this.isPlaying[playerId] = false;
    } else {
      this.stopAllPlayersExcept(playerId);
      if (!audioElement.dataset['hlsInitialized']) {
        const url = this.hlsLocation + element.id + '.m4a/playlist.m3u8';
        audioElement.dataset['hlsInitialized'] = 'true';
        this.editService.playHLSWithElement(url, audioElement);
      } else {
        audioElement.play();
      }
      this.isPlaying[playerId] = true;
    }
  }

  onTimeUpdate(event: Event, playerId: string): void {
    const audio = event.target as HTMLAudioElement;
    this.currentTime[playerId] = audio.currentTime;
  }

  onLoadedMetadata(event: Event, playerId: string): void {
    const audio = event.target as HTMLAudioElement;
    this.duration[playerId] = audio.duration;
  }

  onEnded(_event: Event, playerId: string): void {
    this.isPlaying[playerId] = false;
    this.currentTime[playerId] = 0;
  }

  onSeek(event: Event, audioElement: HTMLAudioElement): void {
    const input = event.target as HTMLInputElement;
    audioElement.currentTime = parseFloat(input.value);
  }

  // ============================================================================
  // Mode édition (finalisation, verrouillage, drag & drop, sélection)
  // ============================================================================

  openFinalizeDialog(): void {
    const dialogRef = this.dialog.open(FinalizeDialogComponent, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'confirm') {
        this.finalizePlaylist(result.password);
      }
    });
  }

  finalizePlaylist(password: string): void {
    this.loading = true;
    const PASSWORD_EDIT_PAGE = 'comite44';

    if (password !== PASSWORD_EDIT_PAGE) {
      this.snackBar.open('Mot de passe incorrect', '', { duration: 5000 });
      this.loading = false;
      return;
    }

    const idPlaylist = this.getPlaylistId();

    this.editService.finalizePlaylist(idPlaylist, this.getStationId())
      .then((res: any) => {
        this.finalizedPlaylistId = res.id;
        this.selectionLimit = res.selection_limit;
        this.isLocked = res.is_locked || false;
        this.isEditMode = true;
        this.sortMediasByPosition();
        this.mediasMatTable = new MatTableDataSource<any>(this.medias);
        this.mediasMatTable.paginator = this.paginator;
        this.snackBar.open('Mode édition activé');
        this.loading = false;
      })
      .catch(err => {
        console.error('Erreur lors de la finalisation:', err);
        this.snackBar.open('Erreur lors de la finalisation', '', { duration: 5000 });
        this.loading = false;
      });
  }

  protected sortMediasByPosition(): void {
    this.medias.sort((a: any, b: any) => {
      if (a.position !== undefined && a.position !== null &&
        b.position !== undefined && b.position !== null) {
        return a.position - b.position;
      }
      if (a.position !== undefined && a.position !== null) { return -1; }
      if (b.position !== undefined && b.position !== null) { return 1; }
      const posA = this.getPosition(a.imedia);
      const posB = this.getPosition(b.imedia);
      if (posA === 0 && posB === 0) { return 0; }
      if (posA === 0) { return 1; }
      if (posB === 0) { return -1; }
      return posA - posB;
    });
  }

  drop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) { return; }
    moveItemInArray(this.medias, event.previousIndex, event.currentIndex);
    this.updateAllPositions();
  }

  updateAllPositions(): void {
    this.medias.forEach((media: any, index: number) => { media.position = index + 1; });
    const positionsString = this.medias.map((m: any) => m.imedia).join(',');

    this.editService.updatePlaylistPositions(this.finalizedPlaylistId!, positionsString)
      .then(() => {
        this.mediasMatTable = new MatTableDataSource<any>(this.medias);
        this.mediasMatTable.paginator = this.paginator;
        this.snackBar.open('Positions mises à jour');
      })
      .catch(err => {
        console.error('Erreur lors de la mise à jour des positions:', err);
        this.snackBar.open('Erreur lors de la mise à jour', '', { duration: 3000 });
      });
  }

  // ============================================================================
  // Réordonnancement manuel (flèches haut/bas + position éditable)
  // ============================================================================

  canReorder(): boolean {
    return this.isEditMode && this.isEffectivelyLocked;
  }

  /**
   * Restriction du drag & drop à la poignée uniquement.
   * `cdkDragHandle` seul ne suffit pas ici : la poignée vit dans une cellule
   * `matCellDef` séparée de la ligne `cdkDrag` (limitation connue d'Angular CDK,
   * voir angular/components#30576), donc la ligne resterait draggable partout.
   * À la place, le drag est désactivé par défaut et n'est armé que pendant le
   * mousedown/touchstart sur la poignée, puis désarmé au relâchement.
   */
  dragHandleArmed = false;

  armDragHandle(): void {
    this.dragHandleArmed = true;
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  disarmDragHandle(): void {
    this.dragHandleArmed = false;
  }

  moveMediaUp(element: any): void {
    if (!this.canReorder()) { return; }
    const index = this.medias.findIndex((m: any) => m.imedia === element.imedia);
    if (index <= 0) { return; }
    moveItemInArray(this.medias, index, index - 1);
    this.updateAllPositions();
  }

  moveMediaDown(element: any): void {
    if (!this.canReorder()) { return; }
    const index = this.medias.findIndex((m: any) => m.imedia === element.imedia);
    if (index < 0 || index >= this.medias.length - 1) { return; }
    moveItemInArray(this.medias, index, index + 1);
    this.updateAllPositions();
  }

  onPositionInput(element: any, event: Event): void {
    if (!this.canReorder()) { return; }
    const input = event.target as HTMLInputElement;
    const targetPosition = parseInt(input.value, 10);
    const fromIndex = this.medias.findIndex((m: any) => m.imedia === element.imedia);

    if (fromIndex < 0 || isNaN(targetPosition) || targetPosition < 1) {
      input.value = String(fromIndex + 1);
      this.snackBar.open('Position invalide — merci de saisir un nombre entier supérieur ou égal à 1', '', { duration: 3000 });
      return;
    }

    const toIndex = Math.min(targetPosition, this.medias.length) - 1;
    if (toIndex === fromIndex) { return; }

    moveItemInArray(this.medias, fromIndex, toIndex);
    this.updateAllPositions();
  }

  onSelectionLimitInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (value === '' || value === null || value === undefined) {
      this.selectionLimit = null;
      this.selectionLimitSubject.next(null);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 1) {
        this.selectionLimit = numValue;
        this.selectionLimitSubject.next(numValue);
      }
    }
  }

  saveSelectionLimit(value: number | null): void {
    if (!this.finalizedPlaylistId) { return; }
    this.editService.updateSelectionLimit(this.finalizedPlaylistId, value)
      .then(() => {
        if (value === null) {
          this.snackBar.open('Limite de sélection supprimée', '', { duration: 2000 });
        } else {
          this.snackBar.open(`Limite de sélection : ${value} titres`, '', { duration: 2000 });
        }
      })
      .catch(err => {
        console.error('Erreur lors de la mise à jour de la limite:', err);
        this.snackBar.open('Erreur lors de la mise à jour', '', { duration: 3000 });
      });
  }

  togglePlaylistLock(): void {
    const newLockState = !this.isLocked;
    if (!newLockState) {
      const dialogRef = this.dialog.open(UnlockConfirmDialogComponent, {
        width: '450px',
        disableClose: true
      });
      dialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) { this.performLockToggle(newLockState); }
      });
    } else {
      this.performLockToggle(newLockState);
    }
  }

  private performLockToggle(newLockState: boolean): void {
    let positions: string | null = null;
    if (newLockState) {
      const mediasWithPosition = this.medias
        .map((media: any) => ({ imedia: media.imedia, position: this.getPosition(media.imedia) }))
        .filter((m: any) => m.position > 0)
        .sort((a: any, b: any) => a.position - b.position);
      positions = mediasWithPosition.map((m: any) => m.imedia).join(',');
    }

    this.editService.setPlaylistLock(this.finalizedPlaylistId!, newLockState, positions)
      .then(() => {
        this.isLocked = newLockState;
        if (newLockState) {
          const positionsArray = positions!.split(',').map((id: string) => parseInt(id, 10));
          const positionsMap = new Map<number, number>();
          positionsArray.forEach((imedia: number, index: number) => { positionsMap.set(imedia, index + 1); });
          this.medias = this.medias.map((media: any) => ({
            ...media,
            position: positionsMap.has(media.imedia) ? positionsMap.get(media.imedia) : null
          }));
          this.snackBar.open('Votes verrouillés - Positions enregistrées');
        } else {
          this.medias = this.medias.map((media: any) => ({ ...media, position: null }));
          this.sortMediasByPosition();
          // Le déverrouillage rouvre le travail du comité : le statut d'envoi est réinitialisé côté serveur.
          this.isSentWinmedia = false;
          this.isSentLaravel = false;
          this.sentAt = null;
          this.snackBar.open('Votes déverrouillés - Positions réinitialisées');
        }
        this.mediasMatTable = new MatTableDataSource<any>(this.medias);
        this.mediasMatTable.paginator = this.paginator;
      })
      .catch(err => {
        console.error('Erreur lors du verrouillage:', err);
        this.snackBar.open('Erreur lors du verrouillage', '', { duration: 3000 });
      });
  }

  // ============================================================================
  // Envoi de la sélection finalisée (Winmedia + Le Son Unique)
  // ============================================================================

  openSendFinalizedPlaylistDialog(): void {
    if (!this.isLocked || (this.isSentWinmedia && this.isSentLaravel)) { return; }

    const dialogRef = this.dialog.open(SendFinalizedPlaylistConfirmDialogComponent, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) { this.sendFinalizedPlaylist(); }
    });
  }

  sendFinalizedPlaylist(): void {
    if (!this.finalizedPlaylistId) { return; }

    this.loading = true;
    this.editService.sendFinalizedPlaylist(this.finalizedPlaylistId)
      .then((res: any) => {
        this.isSentWinmedia = res.isSentWinmedia || false;
        this.isSentLaravel = res.isSentLaravel || false;
        this.sentAt = res.sentAt || null;

        const winmediaStatus = res.winmedia?.success ? 'OK' : `échec (${res.winmedia?.error || 'erreur inconnue'})`;
        const laravelStatus = res.laravel?.success ? 'OK' : `échec (${res.laravel?.error || 'erreur inconnue'})`;

        if (this.isSentWinmedia && this.isSentLaravel) {
          this.snackBar.open('Sélection finalisée envoyée avec succès', '', { duration: 5000 });
        } else {
          this.snackBar.open(
            `Envoi partiel — Winmedia: ${winmediaStatus} / Le Son Unique: ${laravelStatus}`,
            '', { duration: 8000 }
          );
        }
        this.loading = false;
      })
      .catch(err => {
        console.error('Erreur lors de l\'envoi de la sélection finalisée:', err);
        this.snackBar.open('Erreur lors de l\'envoi de la sélection finalisée', '', { duration: 5000 });
        this.loading = false;
      });
  }

  isInSelection(element: any): boolean {
    if (!this.selectionLimit || !element.position) { return false; }
    return element.position <= this.selectionLimit;
  }

  isInSelectionDynamic(element: any): boolean {
    if (!this.selectionLimit) { return false; }
    const position = this.getPosition(element.imedia);
    return position > 0 && position <= this.selectionLimit;
  }

  exitEditMode(): void {
    this.isEditMode = false;
    this.mediasMatTable = new MatTableDataSource<any>(this.medias);
    this.mediasMatTable.paginator = this.paginator;
    this.mediasMatTable.sort = this.sort;
    this.mediasMatTable.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'modify': return new Date(item.modify);
        case 'score': return this.getBayesianSortValue(item.imedia);
        case 'average': return this.getAverageSortValue(item.imedia);
        case 'release': return item.release_date;
        case 'performer_title': return item.performer + item.title;
        case 'vote': return this.getUserScore(item.imedia);
        case 'votes_detail': return this.getVoteCount(item.imedia);
        case 'position': return item.position !== undefined && item.position !== null
          ? item.position
          : this.getPositionSortValue(item.imedia);
        default: return item[property];
      }
    };
  }
}
